import React, { useMemo } from 'react';
import {Box, Typography, Grid} from '@mui/material';
import PartnerDashboardShell from '../components/PartnerDashboard.comp';
import { useDashboard } from '../../../context/DashboardContext';
import {isSuppressedKpi, SUPPRESSED_COPY} from '../../DashboardCommon/dashboardCards';
import { READINESS_BAND_COLORS, PARTICIPATION_BAND_COLORS, DEPLOYMENT_ACTION_BAND_COLORS } from '../constants';

import {UNIT, fmtValue} from '../../DashboardCommon/units';
const NAVY   = '#052F44';
const ORANGE = '#FF6900';
const AMBER  = '#EFA020';
const CORAL  = '#E04535';

/* ══════════════════════════════════════════════════════════════════
   KPI CARD  — top accent bar + label (badge or small-caps) + value
   ══════════════════════════════════════════════════════════════════ */
const KpiCard = ({
    topColor,
    labelBadge,      // if true → renders label on navy bg badge
    labelColor,      // used when labelBadge is false (small-caps text color)
    label,
    value,
    valueBoxed,      // if true → wraps value in a navy box
    subtitle,
    subBadge,        // text below value
    pillLabel,
    pillColor,
}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)',
        borderRadius: '12px',
        backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    }}>
        {/* Top accent bar */}
        <Box sx={{backgroundColor: topColor, height: '6px', flexShrink: 0}}/>

        <Box sx={{p: 2.5, display: 'flex', flexDirection: 'column', flex: 1}}>
            {/* Label — badge variant or small-caps text */}
            {labelBadge ? (
                <Box sx={{
                    alignSelf: 'flex-start',
                    backgroundColor: NAVY,
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: 700,
                    px: 2,
                    py: 0.5,
                    borderRadius: '6px',
                    mb: 1.5,
                }}>
                    {label}
                </Box>
            ) : (
                <Typography sx={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: labelColor || NAVY,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    mb: 1.5,
                }}>
                    {label}
                </Typography>
            )}

            {/* Primary value */}
            {valueBoxed ? (
                <Box sx={{
                    alignSelf: 'flex-start',
                    backgroundColor: NAVY,
                    color: '#ffffff',
                    fontSize: '40px',
                    fontWeight: 800,
                    px: 2.5,
                    py: 0.5,
                    borderRadius: '8px',
                    lineHeight: 1.2,
                    mb: 0.5,
                }}>
                    {value}
                </Box>
            ) : (
                <Typography sx={{
                    fontSize: {xs: '36px', md: '44px'},
                    fontWeight: 800,
                    color: NAVY,
                    lineHeight: 1,
                    mb: 0.5,
                }}>
                    {value}
                </Typography>
            )}

            {/* Sub-badge / Subtitle text */}
            {subBadge && (
                <Typography sx={{fontSize: '12px', color: '#6A7282', mt: 0.5, mb: 0.5}}>
                    {subBadge}
                </Typography>
            )}

            {/* Subtitle */}
            {subtitle && (
                <Typography sx={{fontSize: '12px', color: '#6A7282', mt: 0.5, flex: 1}}>
                    {subtitle}
                </Typography>
            )}

            {/* Full-width status pill at the bottom */}
            {pillLabel && (
                <Box sx={{
                    mt: 'auto',
                    pt: 1.5,
                }}>
                    <Box sx={{
                        display: 'inline-block',
                        backgroundColor: pillColor,
                        color: '#ffffff',
                        fontSize: '12px',
                        fontWeight: 700,
                        px: 1.5,
                        py: 0.4,
                        borderRadius: '20px',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        minWidth: '85px',
                    }}>
                        {pillLabel}
                    </Box>
                </Box>
            )}
        </Box>
    </Box>
);

/* ══════════════════════════════════════════════════════════════════
   PORTFOLIO AT A GLANCE
   ══════════════════════════════════════════════════════════════════ */
const PortfolioGlance = ({ stats, title }) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)',
        borderRadius: '12px',
        backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        p: 3,
        height: '100%',
    }}>
        <Typography sx={{fontSize: '18px', fontWeight: 700, color: NAVY, mb: 2.5}}>
            {title || 'Portfolio at a glance'}
        </Typography>
        <Grid container spacing={2}>
            {stats.map((stat) => (
                <Grid item xs={6} md={3} key={stat.label}>
                    <Typography sx={{
                        fontSize: '34px',
                        fontWeight: 800,
                        color: NAVY,
                        lineHeight: 1,
                        mb: 0.4,
                    }}>
                        {stat.value}
                    </Typography>
                    <Typography sx={{
                        fontSize: '10px',
                        fontWeight: 700,
                        color: '#6A7282',
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                    }}>
                        {stat.label}
                    </Typography>
                </Grid>
            ))}
        </Grid>
    </Box>
);

/* ══════════════════════════════════════════════════════════════════
   LEFT LINE CARD (HTML content)
   ══════════════════════════════════════════════════════════════════ */
const LeftLineCardComp = ({ html, accentColor }) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)',
        borderRadius: '12px',
        backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        p: 3,
        height: '100%',
        borderLeft: `6px solid ${accentColor || '#000000'}`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        '& h1': {
            fontSize: '18px',
            fontWeight: 700,
            color: NAVY,
            mt: 0,
            mb: 1.5,
        },
        '& p': {
            fontSize: '14px',
            color: '#4B5563',
            m: 0,
            lineHeight: 1.5,
        }
    }}>
        <div dangerouslySetInnerHTML={{__html: html}} />
    </Box>
);

/* ══════════════════════════════════════════════════════════════════
   DECISION GUIDANCE FOOTER
   ══════════════════════════════════════════════════════════════════ */
const DecisionGuidance = ({text, html}) => (
    <Box sx={{
        mt: 4,
        p: {xs: 2, md: 2.5},
        borderRadius: '0 8px 8px 0',
        backgroundColor: 'var(--brand-surface-subtle, #EFF6FF)',
        borderLeft: `4px solid ${NAVY}`,
        border: '1px solid #BFDBFE',
        borderLeftColor: NAVY,
        borderLeftWidth: '4px',
        borderLeftStyle: 'solid',
    }}>
        <Typography component="div" sx={{fontSize: '13px', color: '#374151', lineHeight: 1.7}}>
            <strong style={{color: NAVY}}>Decision guidance:&nbsp;</strong>
            {html ? <span dangerouslySetInnerHTML={{__html: html}} /> : text}
        </Typography>
    </Box>
);

/* ══════════════════════════════════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════════════════════════════════ */
const PORTFOLIO_OVERVIEW_SECTION_CODE = 'S_4';
const CARD_SECTIONS = [PORTFOLIO_OVERVIEW_SECTION_CODE];

const PortfolioOverviewPage = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTIONS });

    const sectionData = useMemo(() => {
        return dashboardData?.cards?.sections?.find(
            (section) => section.section_code === PORTFOLIO_OVERVIEW_SECTION_CODE
        );
    }, [dashboardData]);


    const s4Cards = sectionData?.cards || [];

    // Extract dynamic TitleCard if available
    const titleCard = s4Cards.find(c => c.card_type === 'TitleCard');
    const headerTitle = titleCard?.title || "Portfolio Executive Summary";
    const headerSubtitle = titleCard?.footer || "Which countries, clients and products need partner attention?";

    // Extract dynamic ORGCard data if available
    const orgCard = s4Cards.find(c => c.card_type === 'ORGCard');
    const dynamicInfo = orgCard?.table_objects ? orgCard.table_objects.map(obj => ({
        label: obj.column_name,
        value: obj.values?.[0] || '—'
    })) : null;

    // Helper to map band to color
    const getBandColor = (band) => {
        if (!band) return NAVY;
        const b = band.toUpperCase();
        if (b.includes('MIXED') || b.includes('COVERAGE')) return AMBER;
        if (b.includes('ACT') || b.includes('CYCLE')) return CORAL;
        if (b.includes('PREVALENCE')) return CORAL;
        if (b.includes('HIGH') || b.includes('STRENGTH') || b.includes('ADVANCED')) return READINESS_BAND_COLORS.High;
        if (b.includes('PRIORITY') || b.includes('CONCERN') || b.includes('CRITICAL') || b.includes('POOR')) return CORAL;
        if (b.includes('LOW')) return READINESS_BAND_COLORS.Low; // distinct from Priority Concern
        if (b.includes('MODERATE') || b.includes('AVERAGE')) return READINESS_BAND_COLORS.Moderate; // distinct from Basic
        if (b.includes('BASIC')) return READINESS_BAND_COLORS.Basic;
        return NAVY;
    };

    // Extract dynamic KPICards if available
    const kpiCards = s4Cards.filter(c => c.card_type === 'KPICard') || [];
    const displayKpiCards = [];

    const mainKpiCodes = ['C_3', 'C_4', 'C_5', 'C_6'];
    const mainKpis = kpiCards.filter(c => mainKpiCodes.includes(c.card_code));

    if (mainKpis.length > 0) {
        mainKpis.forEach(c => {
            let topColor = NAVY;
            let labelColor = NAVY;
            let labelBadge = false;
            let valueBoxed = false;
            let val = c.kpi_value;
            let subtitle = '';
            let subBadge = undefined;

            const addInfo = c.kpi_add_infos?.[0];

            if (c.card_code === 'C_3') {
                topColor = AMBER;
                val = c.kpi_value;
                const regInfo = c.kpi_add_infos?.find(
                    (info) => info.info_name?.toLowerCase().includes('register')
                ) || addInfo;
                if (regInfo && regInfo.info_value !== undefined && regInfo.info_name) {
                    subtitle = `out of ${regInfo.info_value} ${regInfo.info_name.toLowerCase()} schools`;
                } else if (addInfo) {
                    subtitle = addInfo.info_name;
                }
            } else if (c.card_code === 'C_4') {
                val = c.kpi_value;
                if (val !== null && val !== undefined && val !== '') {
                    val = fmtValue(val, UNIT.PERCENT);
                }
                const assessedInfo = c.kpi_add_infos?.find(
                    (info) => info.info_name?.toLowerCase().includes('assess')
                );
                const registeredInfo = c.kpi_add_infos?.find(
                    (info) => info.info_name?.toLowerCase().includes('register')
                );
                if (assessedInfo && registeredInfo) {
                    subtitle = `${assessedInfo.info_value} (${assessedInfo.info_name?.toLowerCase()}) / ${registeredInfo.info_value} (${registeredInfo.info_name?.toLowerCase()})`;
                } else if (c.kpi_add_infos && c.kpi_add_infos.length >= 2) {
                    const info1 = c.kpi_add_infos[0];
                    const info2 = c.kpi_add_infos[1];
                    subtitle = `${info1.info_value} (${info1.info_name?.toLowerCase()}) / ${info2.info_value} (${info2.info_name?.toLowerCase()})`;
                } else if (addInfo) {
                    subtitle = addInfo.info_name;
                }
            } else if (c.card_code === 'C_5') {
                topColor = DEPLOYMENT_ACTION_BAND_COLORS['High risk'] || CORAL;
                val = c.kpi_value;
                const subObj = c.kpi_add_infos?.find(
                    (info) => info.info_name?.toLowerCase() === 'subtitle'
                ) || addInfo;
                subtitle = subObj ? (subObj.info_value || subObj.info_name) : '';
            } else if (c.card_code === 'C_6') {
                topColor = CORAL;
                labelColor = ORANGE;
                val = c.kpi_value;
                subtitle = addInfo ? addInfo.info_name : '';
            }

            let pillLabel = addInfo?.band || c.kpi_band || c.kpi_data?.band || '';
            if (c.card_code === 'C_3' || c.card_code === 'C_5') {
                pillLabel = '';
            } else if (c.card_code === 'C_6') {
                pillLabel = 'Act this cycle';
            }
            const pillColor = c.card_code === 'C_4'
                ? (PARTICIPATION_BAND_COLORS[pillLabel] || Object.entries(PARTICIPATION_BAND_COLORS).find(([k]) => k.toLowerCase() === pillLabel?.toLowerCase())?.[1] || getBandColor(pillLabel))
                : getBandColor(pillLabel);

            if (c.card_code === 'C_4') {
                topColor = pillColor;
            }

            // A KPI under the n floor (suppressed) reads "—" with the reason underneath and no pill;
            // a value that is not sent reads "—" (never the string "null").
            const suppressed = isSuppressedKpi(c);
            if (suppressed) {
                subtitle = SUPPRESSED_COPY;
                pillLabel = '';
            }
            displayKpiCards.push({
                label: c.kpi_name,
                value: suppressed || val === null || val === undefined ? '—' : (typeof val === 'number' ? String(val.toFixed(2).replace(/\.00$/, '')) : String(val)),
                subtitle: subtitle,
                subBadge,
                pillLabel,
                pillColor,
                topColor,
                labelColor,
                labelBadge,
                valueBoxed
            });
        });
    }
    // Extract Portfolio at a glance stats
    const glanceStatsCodes = ['C_8', 'C_9', 'C_10', 'C_11'];
    const glanceCards = s4Cards.filter(c => glanceStatsCodes.includes(c.card_code));

    const glanceStats = [];
    if (glanceCards.length > 0) {
        glanceCards.forEach(c => {
            glanceStats.push({
                value: c.kpi_value === null || c.kpi_value === undefined ? '—' : String(c.kpi_value),
                label: c.kpi_name
            });
        });
        // if (!glanceStats.some(s => s.label.toUpperCase().includes('PACKAGE'))) {
        //     glanceStats.push({ value: '6', label: 'Active Packages' });
        // }
    }

    // Extract dynamic title card C_7 if available
    const c7TitleCard = s4Cards.find(c => c.card_code === 'C_7');
    const glanceTitle = c7TitleCard?.title || "Portfolio at a glance";

    return (
        <PartnerDashboardShell>
            {/* Title */}
            <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, mb: 0.5}}>
                {headerTitle}
            </Typography>

            {/* Subtitle */}
            <Typography sx={{fontSize: '14px', fontStyle: 'italic', color: '#4B5563', mb: 3}}>
                {headerSubtitle}
            </Typography>

            {/* Dynamic Info Strip (ORGCard) */}
            {/* {dynamicInfo && (
                <Box sx={{
                    border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 2.5, mb: 3,
                }}>
                    <Grid container spacing={2}>
                        {dynamicInfo.map((i) => (
                            <Grid item xs={6} md={3} key={i.label}>
                                <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#6A7282', letterSpacing: '0.8px', textTransform: 'uppercase', mb: 0.5}}>
                                    {i.label}
                                </Typography>
                                <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY}}>{i.value}</Typography>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )} */}

            {/* ── KPI cards ── */}
            <Grid container spacing={2.5} sx={{mb: 3}}>
                {displayKpiCards.map((kpi, idx) => (
                    <Grid item xs={12} md={12 / displayKpiCards.length} key={kpi.label || idx}>
                        <KpiCard
                            topColor={kpi.topColor}
                            labelColor={kpi.labelColor}
                            labelBadge={kpi.labelBadge}
                            label={kpi.label}
                            value={kpi.value}
                            valueBoxed={kpi.valueBoxed}
                            subtitle={kpi.subtitle}
                            subtitleBand={kpi.subtitleBand}
                            subBadge={kpi.subBadge}
                            pillLabel={kpi.pillLabel}
                            pillColor={kpi.pillColor}
                            getBandColor={getBandColor}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* ── Bottom panel ── */}
            <Grid container spacing={2.5}>
                <Grid item xs={12}>
                    <PortfolioGlance stats={glanceStats} title={glanceTitle}/>
                </Grid>
                {/* <Grid item xs={12} md={7}>
                    <ClientsAttention/>
                </Grid> */}
            </Grid>
        </PartnerDashboardShell>
    );
};

export default PortfolioOverviewPage;
